import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Form, Modal, Nav, Table } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import Header from "../layouts/Header";

const STATUS = [
  ["present", "มา"],
  ["absent", "ขาด"],
  ["late", "สาย"],
  ["leave", "ลา"],
];

const today = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export default function ClubWorkspace() {
  const { clubYearId } = useParams();
  const { setLoading } = useLoading();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("attendance");
  const [semester, setSemester] = useState("1");
  const [sessionDate, setSessionDate] = useState(today());
  const [openRounds, setOpenRounds] = useState([]);
  const [sessionRoundId, setSessionRoundId] = useState("");
  const [topic, setTopic] = useState("");
  const [attendance, setAttendance] = useState({});
  const [assessment, setAssessment] = useState({});
  const [notes, setNotes] = useState({});
  const [editSession, setEditSession] = useState(null);
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, memberRes, historyRes, roundRes] = await Promise.all([
        axiosInstance.get("/api/clubs/my-clubs"),
        axiosInstance.get(`/api/clubs/offerings/${clubYearId}/members`),
        axiosInstance.get(`/api/clubs/offerings/${clubYearId}/attendance`),
        axiosInstance.get(`/api/clubs/offerings/${clubYearId}/open-session-rounds`),
      ]);
      setClub(myRes.data.find(c => String(c.club_year_id) === String(clubYearId)) || null);
      const active = memberRes.data.filter(m => m.enrollment_status === "active");
      setMembers(active);
      setHistory(historyRes.data);
      setOpenRounds(roundRes.data);
      setAttendance(Object.fromEntries(active.map(m => [m.club_enrollment_id, "present"])));
      setAssessment(Object.fromEntries(active.map(m => [
        m.club_enrollment_id,
        m[`assessment_sem${semester}`] || "pending"
      ])));
    } catch (error) {
      Swal.fire("ผิดพลาด", error.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ", "error");
    } finally { setLoading(false); }
  }, [clubYearId, semester, setLoading]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setAssessment(Object.fromEntries(members.map(m => [
      m.club_enrollment_id, m[`assessment_sem${semester}`] || "pending"
    ])));
  }, [semester, members]);

  const attendanceTotals = useMemo(() => members.reduce((totals, m) => {
    totals.present += Number(m.presents || 0);
    totals.absent += Number(m.absents || 0);
    totals.late += Number(m.lates || 0);
    totals.leave += Number(m.leaves || 0);
    return totals;
  }, { present: 0, absent: 0, late: 0, leave: 0 }), [members]);

  const saveAttendance = async (event) => {
    event.preventDefault();
    try {
      await axiosInstance.post(`/api/clubs/offerings/${clubYearId}/attendance`, {
        club_session_round_id: Number(sessionRoundId), session_topic: topic,
        records: members.map(m => ({
          club_enrollment_id: m.club_enrollment_id,
          attendance_status: attendance[m.club_enrollment_id] || "present"
        }))
      });
      setTopic("");
      await load();
      Swal.fire({ icon: "success", title: "บันทึกเช็คชื่อแล้ว", timer: 1200, showConfirmButton: false });
    } catch (error) {
      const code = error.response?.data?.code;
      const text = code === "SESSION_ROUND_NOT_OPEN" ? "ฝ่ายการเรียนการสอนยังไม่ได้เปิดรอบนี้"
        : code === "ATTENDANCE_EXISTS" ? "มีการเช็คชื่อชุมนุมในรอบนี้แล้ว"
        : error.response?.data?.message || "บันทึกไม่สำเร็จ";
      Swal.fire("บันทึกไม่ได้", text, "error");
    }
  };

  const searchStudents = async (event) => {
    event?.preventDefault();
    if (studentQuery.trim().length < 2) return;
    try {
      const { data } = await axiosInstance.get(`/api/clubs/offerings/${clubYearId}/students/search`, { params: { q: studentQuery.trim() } });
      setStudentResults(data);
    } catch (error) { Swal.fire("ค้นหาไม่สำเร็จ", error.response?.data?.message || "เกิดข้อผิดพลาด", "error"); }
  };

  const enrollStudent = async (student) => {
    try {
      await axiosInstance.post(`/api/clubs/offerings/${clubYearId}/enrollments`, { st_id: student.st_id });
      setStudentQuery(""); setStudentResults([]);
      await load();
      Swal.fire({ icon: "success", title: "รับนักเรียนเข้าชุมนุมแล้ว", timer: 1200, showConfirmButton: false });
    } catch (error) { Swal.fire("รับสมัครไม่สำเร็จ", error.response?.data?.message || "เกิดข้อผิดพลาด", "error"); }
  };
  const saveAssessments = async () => {
    try {
      await axiosInstance.put(`/api/clubs/offerings/${clubYearId}/assessments`, {
        semester,
        records: members.map(m => ({
          club_enrollment_id: m.club_enrollment_id,
          assessment_result: assessment[m.club_enrollment_id] || "pending",
          assessment_note: notes[m.club_enrollment_id] || ""
        }))
      });
      await load();
      Swal.fire({ icon: "success", title: "บันทึกผลประเมินแล้ว", timer: 1200, showConfirmButton: false });
    } catch (error) { Swal.fire("บันทึกไม่สำเร็จ", error.response?.data?.message || "เกิดข้อผิดพลาด", "error"); }
  };

  const openSession = async (sessionId) => {
    try {
      const { data } = await axiosInstance.get(`/api/clubs/attendance/${sessionId}`);
      setEditSession({
        ...data[0],
        records: data.map(row => ({
          club_enrollment_id: row.club_enrollment_id,
          st_idnumber: row.st_idnumber,
          st_name: row.st_name,
          attendance_status: row.attendance_status
        }))
      });
    } catch (error) { Swal.fire("ผิดพลาด", "โหลดรายการเช็คชื่อไม่สำเร็จ", "error"); }
  };

  const updateSession = async () => {
    try {
      await axiosInstance.put(`/api/clubs/attendance/${editSession.club_session_id}`, {
        session_topic: editSession.session_topic, records: editSession.records
      });
      setEditSession(null);
      await load();
      Swal.fire({ icon: "success", title: "แก้ไขแล้ว", timer: 1000, showConfirmButton: false });
    } catch (error) { Swal.fire("แก้ไขไม่สำเร็จ", error.response?.data?.message || "เกิดข้อผิดพลาด", "error"); }
  };

  return (
    <>
      <Header />
      <main className="main main-app p-3 p-lg-4">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <div className="mb-2"><Link to="/clubs/my" className="text-decoration-none">← ชุมนุมของฉัน</Link></div>
          <h4 className="main-title mb-1">{club?.club_name || "ชุมนุม"}</h4>
          <div className="text-secondary">สมาชิก {members.length} คน · {club?.advisor_role === "lead" ? "ครูผู้ดูแลหลัก" : "ครูผู้ช่วย"}</div>
        </div>
        <Badge bg={club?.club_gender === "m" ? "primary" : "danger"}>{club?.club_gender === "m" ? "ชาย" : "หญิง"}</Badge>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {STATUS.map(([key, label]) => <Badge key={key} bg={key === "present" ? "success" : key === "absent" ? "danger" : key === "late" ? "warning" : "info"} className="p-2">{label}สะสม {attendanceTotals[key]}</Badge>)}
      </div>

      <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
        <Nav.Item><Nav.Link eventKey="attendance">เช็คชื่อ</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="assessment">ประเมินผล</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="history">ประวัติเช็คชื่อ</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="members">สมาชิก</Nav.Link></Nav.Item>
      </Nav>

      {tab === "attendance" && (
        <Card className="card-one">
          <Form onSubmit={saveAttendance}>
            <Card.Header className="d-flex flex-wrap gap-2 align-items-center">
              <Card.Title as="h6" className="mb-0 me-auto">เช็คชื่อชุมนุม</Card.Title>
              <Form.Select size="sm" required value={sessionRoundId} onChange={e => { const round = openRounds.find(r => String(r.club_session_round_id) === e.target.value); setSessionRoundId(e.target.value); if (round) setSemester(String(round.semester)); }} style={{ width: 260 }}><option value="">เลือกรอบที่ฝ่ายการเรียนการสอนเปิด</option>{openRounds.map(r => <option key={r.club_session_round_id} value={r.club_session_round_id}>{String(r.session_date).slice(0, 10)} · ภาค {r.semester} · คาบ {r.period_order}</option>)}</Form.Select>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3"><Form.Label>หัวข้อกิจกรรม</Form.Label><Form.Control required value={topic} onChange={e => setTopic(e.target.value)} placeholder="ระบุหัวข้อกิจกรรมของวันนี้" /></Form.Group>
              <MemberStatusTable members={members} values={attendance} onChange={(id, value) => setAttendance(prev => ({ ...prev, [id]: value }))} />
              <div className="text-end"><Button type="submit" disabled={!members.length}>บันทึกเช็คชื่อ</Button></div>
            </Card.Body>
          </Form>
        </Card>
      )}

      {tab === "assessment" && (
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap gap-2 align-items-center">
            <Card.Title as="h6" className="mb-0 me-auto">ประเมินผ่าน / ไม่ผ่าน</Card.Title>
            <Form.Select size="sm" value={semester} onChange={e => setSemester(e.target.value)} style={{ width: 140 }}><option value="1">ภาคเรียน 1</option><option value="2">ภาคเรียน 2</option></Form.Select>
            <Button size="sm" variant="outline-success" onClick={() => setAssessment(Object.fromEntries(members.map(m => [m.club_enrollment_id, "pass"])))}>ผ่านทั้งหมด</Button>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 align-middle">
              <thead><tr><th>รหัส</th><th>ชื่อ</th><th>สรุปเข้าเรียน</th><th>ผล</th><th>หมายเหตุ</th></tr></thead>
              <tbody>{members.map(m => <tr key={m.club_enrollment_id}><td>{m.st_idnumber}</td><td>{m.st_name}</td><td>มา {m.presents || 0} · ขาด {m.absents || 0} · สาย {m.lates || 0} · ลา {m.leaves || 0}</td><td><Form.Select size="sm" value={assessment[m.club_enrollment_id] || "pending"} onChange={e => setAssessment(prev => ({ ...prev, [m.club_enrollment_id]: e.target.value }))}><option value="pending">รอประเมิน</option><option value="pass">ผ่าน</option><option value="fail">ไม่ผ่าน</option></Form.Select></td><td><Form.Control size="sm" value={notes[m.club_enrollment_id] || ""} onChange={e => setNotes(prev => ({ ...prev, [m.club_enrollment_id]: e.target.value }))} /></td></tr>)}</tbody>
            </Table>
            <div className="text-end p-3"><Button onClick={saveAssessments}>บันทึกผลประเมิน</Button></div>
          </Card.Body>
        </Card>
      )}

      {tab === "history" && (
        <Card className="card-one"><Card.Body className="p-0"><Table responsive hover className="mb-0"><thead><tr><th>วันที่</th><th>ภาคเรียน</th><th>หัวข้อ</th><th>ผู้เช็ค</th><th>มา/ขาด/สาย/ลา</th><th /></tr></thead><tbody>{history.map(row => <tr key={row.club_session_id}><td>{String(row.session_date).slice(0, 10)}</td><td>{row.semester}</td><td>{row.session_topic}</td><td>{row.checked_by_name}</td><td>{row.presents || 0}/{row.absents || 0}/{row.lates || 0}/{row.leaves || 0}</td><td><Button size="sm" variant="outline-primary" onClick={() => openSession(row.club_session_id)}>แก้ไข</Button></td></tr>)}</tbody></Table></Card.Body></Card>
      )}

      {tab === "members" && (
        <Card className="card-one">
          <Card.Header><Card.Title as="h6" className="mb-0">สมาชิกชุมนุม</Card.Title></Card.Header>
          <Card.Body>
            <Form onSubmit={searchStudents} className="d-flex gap-2 mb-3">
              <Form.Control placeholder="ค้นหาด้วยเลขประจำตัวหรือชื่อ (อย่างน้อย 2 ตัวอักษร)" value={studentQuery} onChange={e => setStudentQuery(e.target.value)} />
              <Button type="submit">ค้นหา</Button>
            </Form>
            {!!studentResults.length && <div className="border rounded mb-3">{studentResults.map(student => <div key={student.st_id} className="d-flex justify-content-between align-items-center gap-2 p-2 border-bottom"><span>{student.st_idnumber} {student.st_name} ({student.c_class}/{student.r_no}) {student.current_club_name && <Badge bg="warning" text="dark">อยู่ชุมนุม {student.current_club_name}</Badge>}</span><Button size="sm" disabled={!!student.current_club_year_id} onClick={() => enrollStudent(student)}>รับสมัคร</Button></div>)}</div>}
          </Card.Body>
          <Table responsive hover className="mb-0"><thead><tr><th>รหัส</th><th>ชื่อ</th><th>ห้อง</th><th>สมัครโดย</th><th>ผลภาค 1</th><th>ผลภาค 2</th></tr></thead><tbody>{members.map(m => <tr key={m.club_enrollment_id}><td>{m.st_idnumber}</td><td>{m.st_name}</td><td>{m.c_class}/{m.r_no}</td><td>{m.enrollment_source === "portal" ? "นักเรียน" : "ผู้ดูแล"}</td><td>{resultLabel(m.assessment_sem1)}</td><td>{resultLabel(m.assessment_sem2)}</td></tr>)}</tbody></Table>
        </Card>
      )}
      <Modal show={!!editSession} onHide={() => setEditSession(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>แก้ไขเช็คชื่อ {editSession?.session_date && String(editSession.session_date).slice(0, 10)}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3"><Form.Label>หัวข้อ</Form.Label><Form.Control value={editSession?.session_topic || ""} onChange={e => setEditSession(prev => ({ ...prev, session_topic: e.target.value }))} /></Form.Group>
          <MemberStatusTable members={editSession?.records || []} values={Object.fromEntries((editSession?.records || []).map(r => [r.club_enrollment_id, r.attendance_status]))} onChange={(id, value) => setEditSession(prev => ({ ...prev, records: prev.records.map(r => r.club_enrollment_id === id ? { ...r, attendance_status: value } : r) }))} />
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setEditSession(null)}>ยกเลิก</Button><Button onClick={updateSession}>บันทึกการแก้ไข</Button></Modal.Footer>
      </Modal>
      </main>
    </>
  );
}

function MemberStatusTable({ members, values, onChange }) {
  return <Table responsive hover className="align-middle"><thead><tr><th>รหัส</th><th>ชื่อ</th><th>สถานะ</th></tr></thead><tbody>{members.map(m => <tr key={m.club_enrollment_id}><td>{m.st_idnumber}</td><td>{m.st_name}</td><td><div className="d-flex flex-wrap gap-2">{STATUS.map(([key, label]) => <Form.Check key={key} inline type="radio" name={`att-${m.club_enrollment_id}`} id={`att-${m.club_enrollment_id}-${key}`} label={label} checked={(values[m.club_enrollment_id] || "present") === key} onChange={() => onChange(m.club_enrollment_id, key)} />)}</div></td></tr>)}</tbody></Table>;
}

function resultLabel(value) {
  if (value === "pass") return "ผ่าน";
  if (value === "fail") return "ไม่ผ่าน";
  return "รอประเมิน";
}
